import { useEffect, useMemo } from 'react'
import Button, { Label } from 'components/home/common/Button'
import { useState } from 'react'
import { formatShortCurrency } from 'data/helpers/currency'
import BookingRequestModal from 'components/modals/home/property/BookingRequestModal'
import { useSelector } from 'react-redux'
import { formatDateRangeReadable, formatDefault } from 'data/helpers/dates'
import { PropertyPageQuery } from 'gql/graphql'
import { RootState } from 'store'
import { useQuery } from 'urql'
import { graphql } from 'gql'
import Headers from 'components/home/common/typography/Headers'
import classNames from 'classnames'
import { ExclaimCircleIcon, LoadingIcon } from 'components/icons'

interface PropertyStickyFooterProps {
  property: NonNullable<PropertyPageQuery['property']>
}

const PropertyStickyFooter = ({
  property,
}: PropertyStickyFooterProps) => {
  const [bookingRequestModalOpen, setBookingRequestModalOpen] = useState(false)

  const { arrival, departure } = useSelector((state: RootState) => ({
    arrival: state.bookingRequest.arrival,
    departure: state.bookingRequest.departure,
  }))

  const [inquiryString, setInquiryString] = useState<string | undefined>(undefined)
  const datesFilled = useMemo(() => arrival && departure, [arrival, departure])

  const [{ data, fetching }] = useQuery({
    query: graphql(`
      query Availability ($input: AvailabilityInput!) {
        availability (input: $input) {
          available
          arrival
          departure
          price
          currency
        }
      }
    `),
    variables: {
      input: {
        arrival: formatDefault(arrival),
        departure: formatDefault(departure),
        property_id: property?.id,
      },
    },
    pause: !arrival || !departure || !property?.id,
  })

  useEffect(() => {
    setInquiryString(`${formatDateRangeReadable(arrival, departure)} | ${data?.availability?.available
      ? data?.availability?.price
        ? 'Rent: ' + formatShortCurrency(data?.availability?.price, data?.availability?.currency)
        : 'Price upon request'
      : 'Add dates for price'}`)
  }, [arrival, departure, data?.availability?.price])

  return (
    <div className="flex justify-center lg:hidden">
      <div className="w-full px-25">
        <div className={classNames(
          'flex w-full my-auto gap-x-20',
          {
            'flex-col': !datesFilled,
          },
        )}>
          {
            datesFilled && (
              <div className="flex flex-col justify-between xs:basis-2/5 w-max whitespace-nowrap pt-[1px]">
                <Label variant="dark-grey" className={{ label: '!mt-0 !text-left !leading-22' }}>
                  {formatDateRangeReadable(arrival, departure)}
                </Label>

                <Headers.MD Tag="p" className="leading-[28.34px] flex gap-5">
                  {
                    fetching
                      ? <LoadingIcon className="h-24 mx-auto" />
                      : (
                        data?.availability?.available
                          ? (
                            data?.availability?.price
                              ? 'Rent: ' + formatShortCurrency(data?.availability?.price, data?.availability?.currency)
                              : 'Price upon request'
                          )
                          : 'Rent: ' + formatShortCurrency(data?.availability?.price, data?.availability?.currency)
                      )
                  }
                  {
                    (!fetching && !data?.availability?.available)
                      ? <ExclaimCircleIcon className="text-11 text-red-75" />
                      : null
                  }
                </Headers.MD>
              </div>
            )
          }

          <Button
            block
            onClick={() => setBookingRequestModalOpen(true)}
          >
            {
              datesFilled
                ? 'inquire'
                : 'add dates for price'
            }
          </Button>

          {
            !datesFilled && (
              <Label
                className={{
                  label: 'text-center',
                }}
                variant={datesFilled
                  ? 'dark-grey'
                  : 'grey'
                }
              >
                {
                  datesFilled
                    ? inquiryString
                    : (property?.price_range?.min
                      ? `Starting at ${formatShortCurrency(property?.price_range?.min, property?.currency)}/week`
                      : ''
                    )
                }
              </Label>
            )
          }
        </div>
      </div>

      <BookingRequestModal
        isOpen={bookingRequestModalOpen}
        onClose={() => setBookingRequestModalOpen(false)}
        property={property}
      />
    </div>
  )
}

export default PropertyStickyFooter
